import moment from 'moment';

import { ADD_QUOTE_KEYS } from './addQuote.const';
import { DATE_TYPE } from './Steps/addQuoteSteps.const';

/**
 * Add Quote past-date guard — shared by the step forms' yup schemas and by the final-step submit
 * in useSaveNextStep, so both judge "is this date stale?" by exactly the same rule.
 *
 * Needed because the wizard can hold dates the user never typed: Duplicate Quote copies the
 * source quote's pickup/delivery dates verbatim and marks every step complete, and a wizard left
 * open across midnight ages the same way. The submit guard only checked step COMPLETION, so those
 * stale dates went straight to createQuote.
 */

/**
 * Reduce any stored date value to the calendar day the UI actually shows for it.
 *
 * The slice holds two shapes: 'YYYY-MM-DD 00:00:00' (written by the steps' own onSubmit/persist)
 * and whatever the API returned (Duplicate Quote copies `rest.pickupDate` through untouched, which
 * can be a full ISO timestamp). Taking the leading date segment verbatim — rather than letting
 * moment re-interpret the value in the browser's zone — is what `getFormattedDateIgnoringTime`
 * does when rendering these same fields, so the guard can never disagree with the date on screen.
 */
const toCalendarDay = (value: Date | string | null | undefined): string | null => {
    if (!value) return null;

    if (typeof value === 'string') {
        const day = value.split('T')[0].trim().slice(0, 10);

        return moment(day, 'YYYY-MM-DD', true).isValid() ? day : null;
    }

    const parsed = moment(value);

    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

/** Today in the org's display timezone (same fallback chain the step forms use for min dates). */
const todayCalendarDay = (timezone?: string | null): string => moment.tz(timezone || 'UTC').format('YYYY-MM-DD');

/**
 * True when `value` falls on a calendar day BEFORE today. Empty values are NOT past — required-ness
 * is a separate concern owned by each field's own yup rule.
 */
export const isPastCalendarDay = (value: Date | string | null | undefined, timezone?: string | null): boolean => {
    const day = toCalendarDay(value);

    if (!day) return false;

    // String comparison is safe on zero-padded YYYY-MM-DD and matches the existing
    // isBeforePickupWindowEnd check in ConsigneeDetails.
    return day < todayCalendarDay(timezone);
};

interface IFindPastDateStepArgs {
    shipperDetails: any;
    consigneeDetails: any;
    timezone?: string | null;
}

/**
 * First Add Quote step key holding a date that is now in the past, or null when all dates are fine.
 *
 * IMPORTANT: every condition here has a matching `not-in-past` yup test on that step's field —
 * otherwise sending the user back to the step would show a clean form with no red border and the
 * submit would stay blocked with nothing to fix.
 */
export const findPastDateStep = ({ shipperDetails, consigneeDetails, timezone }: IFindPastDateStepArgs): string | null => {
    const isPickupFlexible = shipperDetails?.pickupSensitivity === DATE_TYPE.FLEXIBLE;

    if (isPastCalendarDay(shipperDetails?.pickupDate, timezone) || (isPickupFlexible && isPastCalendarDay(shipperDetails?.pickupEndDate, timezone))) {
        return ADD_QUOTE_KEYS.SHIPPER_DETAILS;
    }

    // An "open" delivery window has no committed date — the step's own date rules skip it too.
    if (consigneeDetails?.deliverySensitivity === DATE_TYPE.OPEN) return null;

    const isDeliveryFlexible = consigneeDetails?.deliverySensitivity === DATE_TYPE.FLEXIBLE;

    if (isPastCalendarDay(consigneeDetails?.deliveryDate, timezone) || (isDeliveryFlexible && isPastCalendarDay(consigneeDetails?.deliveryEndDate, timezone))) {
        return ADD_QUOTE_KEYS.CONSIGNEE_DETAILS;
    }

    return null;
};
